import { rgba } from 'polished';
import React, { MouseEventHandler, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactSelect, { Props as SelectProps } from 'react-select';
import AsyncSelect from 'react-select/async';
import Creatable from 'react-select/creatable';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { FieldContainer, tokens } from '@treams/treams-ui';

export interface ISelectOptionElementProps {
	isFocused: boolean;
	isSelected: boolean;
}

export interface ISelectOptionInnerProps {
	id: string;
	key: string;
	onClick: MouseEventHandler;
	onMouseOver: MouseEventHandler;
	tabIndex: number;
}

interface ISelectorOptionProps extends ISelectOptionElementProps {
	label: string;
	color?: string;
	innerProps: ISelectOptionInnerProps;
}

const DefaultSelectorOptionElement = styled.div<ISelectOptionElementProps>`
	cursor: pointer;
	color: ${rgba(tokens.text.primary, 0.6)};
	font-size: 16px;
	padding: 0 14px;
	border-top: 1px solid ${tokens.border.subtle};

	&:hover {
		background-color: transparent;
		color: ${rgba(tokens.text.primary, 1)};
	}

	${(props) =>
		props.isFocused &&
		`
		background-color: transparent;
		color: ${rgba(tokens.text.primary, 1)};
	`}

	&:active {
		border-top-color: ${tokens.border.subtle};
		background-color: ${tokens.border.subtle};

		& + .react-select__option {
			border-top: 1px solid ${tokens.border.subtle};
		}
	}
`;

const DefaultSelectOption = ({
	label,
	isFocused,
	isSelected,
	innerProps,
}: ISelectorOptionProps) => {
	return (
		<DefaultSelectorOptionElement
			isFocused={isFocused}
			isSelected={isSelected}
			{...innerProps}
		>
			<Flex flexDirection="row" alignItems="center">
				<Box my={3} ml={2} mr={3}>
					{/* TODO: Add color before label */}
					{label}
				</Box>
			</Flex>
		</DefaultSelectorOptionElement>
	);
};

const style = css<SelectProps & { small?: boolean }>`
	.react-select__dropdown-indicator {
		cursor: pointer;
		svg path {
			fill: ${(props) => props.theme.colors.lightBlueDark};
		}
	}

	.react-select__control {
		background-color: ${tokens.surface.secondary};
		border: 1px solid ${(props) => props.theme.colors.lightBlueDark};
		color: ${tokens.text.secondary};
		border-radius: 4px;
		padding: 4px 4px 4px 16px;
		text-align: left;
		font-size: 16px;
		font-family: inherit;
		line-height: 1;
		cursor: text;
		transition:
			box-shadow 70ms ease-out,
			background-color 70ms ease-out,
			border-color 70ms ease-out;

		&.react-select__control--is-disabled {
			background-color: white;
			border-color: rgba(0, 0, 0, 0.1);

			.react-select__placeholder {
				color: rgba(255, 255, 255, 0.2);
			}
		}

		&:hover {
			border-color: ${(props) => props.theme.colors.lightBlueDark};
		}

		&--is-focused {
			background-color: ${(props) => props.theme.colors.white};
			border-color: ${(props) => rgba(props.theme.colors.primary, 0.6)};
			box-shadow: 0 1px 6px 0 ${(props) => rgba(props.theme.colors.primary, 0.6)};

			&:hover {
				border-color: ${(props) => rgba(props.theme.colors.primary, 0.6)};
			}
		}

		&--menu-is-open {
			border-radius: 4px 4px 0 0;
			border-width: 1px 1px 0 1px;
			box-shadow: none;

			.react-select__dropdown-indicator svg path {
				fill: ${(props) => props.theme.colors.primary};
			}
		}
	}

	.react-select__indicator-separator {
		background-color: ${(props) => props.theme.colors.lightBlueDark};
	}

	.react-select__menu {
		margin-top: 0;
		border-radius: 0 0 4px 4px;
		border: 0;
		outline: 0;
		padding: 0;
		border: 1px solid ${(props) => rgba(props.theme.colors.primary, 0.6)};
		border-width: 0 1px 1px 1px;
		box-shadow: none;
		overflow: hidden;
		position: absolute;
		z-index: 20;
	}

	.react-select__menu-list {
		padding: 0;
	}

	.react-select__value-container {
		/* Reset padding on input field, all padding is placed using the control */
		padding: 0;
	}

	.react-select__multi-value {
		border-radius: 15px;
		padding: 4px 6px;
		background-color: ${tokens.text.secondary};
	}

	.react-select__multi-value__label {
		color: ${(props) => props.theme.colors.white};
	}

	.react-select__multi-value__remove {
		border-radius: 10px;

		svg path {
			fill: ${(props) => props.theme.colors.white};
		}

		:hover {
			background-color: ${(props) => props.theme.colors.white};

			svg path {
				fill: ${tokens.text.secondary};
			}
		}
	}

	${(props) =>
		props.small &&
		`
		margin-bottom: 0;
	`}
`;

const SelectElement = styled(ReactSelect)`
	${style}
`;

const AsyncSelectElement = styled(AsyncSelect)`
	${style}
`;

const CreatableElement = styled(Creatable)`
	${style}
`;

const SmallSelectElement = styled(SelectElement)`
	.react-select__control {
		background: white;
		line-height: 12px;
		padding: 0 0 0 7px;
		font-size: 14px;
		font-weight: normal;
	}
`;

interface IProps {
	creatable?: boolean;
	isAsync?: boolean;
	isMulti?: boolean;
	multi?: boolean;
	unspaced?: boolean;
	small?: boolean;
}

export default function Select<T, IsMulti extends boolean = false>({
	creatable,
	isAsync,
	multi,
	unspaced,
	small,
	...restProps
}: SelectProps<T, IsMulti> & IProps) {
	const ref = useRef<ReactSelect>(null);

	const element = () => {
		if (creatable) {
			return CreatableElement;
		}

		if (small) {
			return SmallSelectElement;
		}

		if (isAsync) {
			return AsyncSelectElement;
		}

		return SelectElement;
	};

	const { isDisabled, ...elementProps } = restProps;

	const { t } = useTranslation();

	return (
		<FieldContainer unspaced={unspaced}>
			{React.createElement(element(), {
				classNamePrefix: 'react-select',
				components: {
					Option: DefaultSelectOption,
				} as any,
				placeholder: t('common.select.placeholder'),
				ref: ref as any,
				...elementProps,
				isDisabled,
				isMulti: multi,
			} as any)}
		</FieldContainer>
	);
}
